import { makeAspectCrop } from 'react-image-crop';

export const percToPx = (perc, tot) => Math.round((parseFloat(perc) / 100) * tot);

export const pxToPerc = (px, tot) => Math.min((100 * px) / tot, 100);

const getInitCrop = () => ({
  height: null,
  width: null,
  aspect: null,
  minWidth: null,
  minHeight: null,
  x: null,
  y: null,
  pixelCrop: {},
});

const MAX_PERCENT = 100;

const getCropSize = (size, cropDefaultSelectSize) => getMaxCropPercent(size ? Math.max(size, cropDefaultSelectSize) : cropDefaultSelectSize);

const getMaxCropPercent = (size) => Math.min(MAX_PERCENT, size);

const getInitialPosition = (size) => (100 - size) / 2;

const getInitCropWidth = (cropOpts, imageRatio, image, cropDefaultSelectSize) => {
  let initCropWidth = cropOpts.cropDefaultSelectSize * cropDefaultSelectSize;

  if (imageRatio > 1 && !cropOpts.croppingAspectRatio) {
    initCropWidth =
      cropOpts.cropDefaultSelectSize < 1
        ? (MAX_PERCENT * image.naturalHeight * cropOpts.cropDefaultSelectSize) / image.naturalWidth
        : (MAX_PERCENT * image.naturalHeight) / image.naturalWidth;
  }

  return initCropWidth;
};

export const calculateCrop = (cropOpts, image) => {
  const initCropSize = cropOpts.cropDefaultSelectSize * MAX_PERCENT,
    cropAspectRatio = cropOpts.croppingAspectRatio,
    cropDefaultSelectSize = initCropSize > 1 ? pxToPerc(initCropSize, initCropSize * cropOpts.cropDefaultSelectSize) : getMaxCropPercent(initCropSize),
    imageRatio = image.naturalWidth / image.naturalHeight;

  let crop = getInitCrop();

  if (cropOpts.minImageWidth) {
    // Set minimum width
    crop.minWidth = pxToPerc(cropOpts.minImageWidth, image.naturalWidth);
    crop.width = Math.max(crop.minWidth, cropDefaultSelectSize);
  }

  if (cropOpts.minImageHeight) {
    // Set minimum height
    crop.minHeight = pxToPerc(cropOpts.minImageHeight, image.naturalHeight);
    crop.height = Math.max(crop.minHeight, cropDefaultSelectSize);
  }

  if (cropAspectRatio) {
    crop.aspect = cropAspectRatio;

    if (cropAspectRatio >= 1) {
      // Ratio > 1 means that width is bigger so we set it first and remove height constraints if exist
      crop.width = getCropSize(crop.width, cropDefaultSelectSize);
      if (crop.height) {
        delete crop.height;
        delete crop.minHeight;
      }
    } else if (cropAspectRatio < 1) {
      // Ratio < 1 means that height is bigger so we set it first and remove width constraints if exist
      crop.height = getCropSize(crop.height, cropDefaultSelectSize);
      if (crop.width) {
        delete crop.width;
        delete crop.minWidth;
      }
    }

    crop = makeAspectCrop(crop, imageRatio);

    // Handle edge-cases where makeAspectCrop() produces >100% crop area.
    if (crop.height && crop.height > MAX_PERCENT) {
      crop.height = cropDefaultSelectSize;
      delete crop.width;
      crop = makeAspectCrop(crop, imageRatio);
    }

    if (crop.width && crop.width > MAX_PERCENT) {
      crop.width = cropDefaultSelectSize;
      delete crop.height;
      crop = makeAspectCrop(crop, imageRatio);
    }
  } else {
    // Free crop
    const initCropWidth = getInitCropWidth(cropOpts, imageRatio, image, cropDefaultSelectSize);
    crop.width = getCropSize(cropOpts.cropDefaultSelectSize * crop.width, getMaxCropPercent(initCropWidth));

    // the height is percentage and relative to the width size.
    crop.height = getCropSize(crop.height, cropDefaultSelectSize);
  }

  // Calculate initial position
  if (crop.width && crop.height) {
    crop.x = getInitialPosition(crop.width);
    crop.y = getInitialPosition(crop.height);

    crop.pixelCrop.width = Math.min(percToPx(crop.width, image.naturalWidth), image.naturalWidth);

    /*
		   when the image is landscape and the crop aspect ratio is NOT defined the crop height is relative is image height
		   when the image is portrait and the crop aspect ratio is defined the crop height relative is to image height
		   in other cases , the crop height is relative to the image width ,
		   for example when image is landscape and crop aspect ratio is defined
		 */
    const isRelativeToHeight = !!((imageRatio > 1 && (!cropAspectRatio || cropAspectRatio > 1)) || (imageRatio < 1 && cropAspectRatio));

    crop.pixelCrop.height = Math.min(percToPx(crop.height, isRelativeToHeight ? image.naturalHeight : image.naturalWidth), image.naturalHeight);

    //convert height relative to height size
    crop.height = getMaxCropPercent(cropAspectRatio ? crop.height : (100 * crop.pixelCrop.height) / image.naturalHeight);

    crop.pixelCrop.x = percToPx(crop.x, image.naturalWidth);
    crop.pixelCrop.y = percToPx(crop.y, image.naturalHeight);
  }

  return crop;
};
